import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage, t } from '../utils/i18n';
import { useCart } from '../contexts/CartContext';
import { createCheckoutSession } from '../services/stripeService';
import testStripeConnection from '../utils/stripeTest';

const StripeCheckout = ({ onSuccess, onError, onCancel }) => {
  const { language } = useLanguage();
  const { cartItems, getCartTotal, clearCart } = useCart();
  const [isProcessing, setIsProcessing] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!customerInfo.name.trim()) {
      onError('Por favor ingresa tu nombre completo');
      return false;
    }
    if (!customerInfo.email.trim() || !/\S+@\S+\.\S+/.test(customerInfo.email)) {
      onError('Por favor ingresa un email válido');
      return false;
    }
    if (!customerInfo.phone.trim()) {
      onError('Por favor ingresa tu teléfono');
      return false;
    }
    return true;
  };

  const handleCheckout = async () => {
    if (!validateForm()) {
      return;
    }

    // Debug: Check if environment variables are loaded
    console.log('Environment check:');
    console.log('REACT_APP_STRIPE_PUBLISHABLE_KEY:', process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
    console.log('All env vars:', Object.keys(process.env).filter(key => key.includes('STRIPE')));

    // Test Stripe connection before proceeding
    const stripeTest = await testStripeConnection();
    if (!stripeTest.success) {
      onError(`Stripe initialization failed: ${stripeTest.error}`);
      return;
    }

    setIsProcessing(true);

    try {
      // Create order data
      const orderData = {
        total: getCartTotal(),
        items: cartItems,
        customerEmail: customerInfo.email,
        customerName: customerInfo.name,
        customerPhone: customerInfo.phone,
        orderId: `AKS-${Date.now()}`
      };

      console.log('Creating checkout session with:', orderData);

      // Redirect to Stripe Checkout
      await createCheckoutSession(orderData);
      
      // If we reach here, something went wrong (should have redirected)
      onError('Error al crear la sesión de pago');

    } catch (error) {
      console.error('Checkout error:', error);
      onError(error.message || 'Error al procesar el pago');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-auto p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="text-2xl font-bold text-akasha-brown mb-6 text-center">
          Información del Cliente
        </h2>

        {/* Customer Information Form */}
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-akasha-black mb-2">
              Nombre Completo *
            </label>
            <input
              type="text"
              name="name"
              value={customerInfo.name}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-akasha-brown focus:border-transparent"
              placeholder="Tu nombre completo"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-akasha-black mb-2">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={customerInfo.email}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-akasha-brown focus:border-transparent"
              placeholder="tu@email.com"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-akasha-black mb-2">
              Teléfono *
            </label>
            <input
              type="tel"
              name="phone"
              value={customerInfo.phone}
              onChange={handleInputChange}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-akasha-brown focus:border-transparent"
              placeholder="55 1234 5678"
              required
            />
          </div>
        </div>

        {/* Order Summary */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-akasha-brown mb-3">Resumen del Pedido</h3>
          <div className="space-y-2">
            {cartItems.map(item => (
              <div key={item.id} className="flex justify-between text-sm">
                <span>{item.name} x {item.quantity}</span>
                <span>${(item.price * item.quantity).toLocaleString()}</span>
              </div>
            ))}
          </div>
          <div className="border-t pt-2 mt-2">
            <div className="flex justify-between font-bold">
              <span>Total:</span>
              <span>${getCartTotal().toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Payment Info */}
        <div className="bg-blue-50 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm font-medium text-blue-800">Método de Pago</span>
          </div>
          <p className="text-sm text-blue-700">
            Serás redirigido a la página segura de Stripe donde podrás pagar con:
          </p>
          <ul className="text-sm text-blue-700 mt-2 ml-4">
            <li>• Tarjeta de crédito/débito</li>
            <li>• OXXO (pago en efectivo)</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-3 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            disabled={isProcessing}
          >
            Cancelar
          </button>
          <button
            onClick={handleCheckout}
            disabled={isProcessing}
            className="flex-1 bg-akasha-brown text-white py-3 px-4 rounded-md hover:bg-akasha-brown/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isProcessing ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Procesando...
              </>
            ) : (
              'Continuar al Pago'
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default StripeCheckout; 